// /context/UserContext.js
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from '../services/axios';
import { useAuth } from './AuthContext';

const UserContext = createContext(null);

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [blogs, setBlogs] = useState([]);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  const { isAuthenticated, authChecked } = useAuth();

  // Prevent out-of-order async updates
  const seqRef = useRef(0);

  const fetchUserProfile = useCallback(async (seq) => {
    const res = await axios.get('/api/users/me/');
    if (seq !== seqRef.current) return { user: null, isAdmin: false };

    setUser(res.data);

    const isAdmin = !!(res.data?.is_superuser ?? res.data?.user?.is_superuser);
    setIsPlatformAdmin(isAdmin);

    return { user: res.data, isAdmin };
  }, []);

  const fetchUserBlogs = useCallback(async (seq) => {
    const res = await axios.get('/api/users/me/blogs/');
    if (seq !== seqRef.current) return [];

    const blogList = Array.isArray(res.data)
      ? res.data
      : (res.data?.blogs ?? res.data?.results ?? []);

    setBlogs(blogList);
    return blogList;
  }, []);

  // ✅ NEW: refresh only blogs list
  const refreshBlogs = useCallback(async () => {
    const seq = ++seqRef.current; // invalidate anything in flight
    try {
      return await fetchUserBlogs(seq);
    } catch (err) {
      if (seq === seqRef.current) {
        console.error('❌ Failed to refresh blogs:', err);
        setBlogs([]);
      }
      return [];
    }
  }, [fetchUserBlogs]);

  // (optional) ✅ NEW: refresh only profile
  const refreshProfile = useCallback(async () => {
    const seq = ++seqRef.current;
    try {
      const { user, isAdmin } = await fetchUserProfile(seq);
      return { user, is_platform_admin: isAdmin };
    } catch (err) {
      if (seq === seqRef.current) {
        console.error('❌ Failed to refresh profile:', err);
        setUser(null);
        setIsPlatformAdmin(false);
      }
      return { user: null, is_platform_admin: false };
    }
  }, [fetchUserProfile]);

  const refreshUserData = useCallback(async () => {
    const seq = ++seqRef.current;
    setLoading(true);

    try {
      const [{ isAdmin }, blogList] = await Promise.all([
        fetchUserProfile(seq),
        fetchUserBlogs(seq),
      ]);
      return { blogs: blogList, is_platform_admin: isAdmin };
    } catch (err) {
      if (seq === seqRef.current) {
        console.error('❌ Failed to refresh user data:', err);
        setUser(null);
        setBlogs([]);
        setIsPlatformAdmin(false);
      }
      return { blogs: [], is_platform_admin: false };
    } finally {
      if (seq === seqRef.current) setLoading(false);
    }
  }, [fetchUserProfile, fetchUserBlogs]);

  useEffect(() => {
    if (!authChecked) return;

    if (!isAuthenticated) {
      seqRef.current += 1;
      setUser(null);
      setBlogs([]);
      setIsPlatformAdmin(false);
      setLoading(false);
      return;
    }

    refreshUserData();
  }, [authChecked, isAuthenticated, refreshUserData]);

  return (
    <UserContext.Provider
      value={{
        user,
        blogs,
        isPlatformAdmin,
        loading,
        refreshUserData,
        refreshBlogs,     // ✅ expose
        refreshProfile,   // ✅ optional expose
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const ctx = useContext(UserContext);
  if (!ctx) throw new Error('useUser must be used within a <UserProvider>');
  return ctx;
};
